import React from "react";
import Radio from "Components/DesignSystem/Radio/Radio";
import Search from "Components/DesignSystem/Search/Search";
import Typography from "Components/DesignSystem/Typography/Typography";

/**
 * The category chooser shared by `PdwDownload` and `ReversePDW` (§ 126).
 *
 * Both screens rendered a byte-identical block: a search box, then one heading per
 * category type with a 4-across grid of radios under it. The two callers keep their
 * own filtering, selection state and download logic, which is where the real
 * differences are.
 *
 * Exported as **two pieces rather than one**, because the two screens place the
 * search differently: `PdwDownload` stacks it above the grid, `ReversePDW` sits it
 * beside the "Download Products" heading. One component would have needed a
 * negative-margin hack to pull the field back up into a header it does not own —
 * the sort of thing that survives review and then breaks the first time the heading
 * wraps. Same split as `RestrictionsControl`.
 *
 * **`instanceId` is not decoration.** Both legacy copies hardcoded the literal
 * radio name `optradio`, and `/gms/pdw` mounts them at the same time (the page renders
 * `ReversePDW` permanently, and opening a download dialog adds a second radio
 * group). A native radio group is scoped by NAME across the whole document, so
 * choosing a category in the dialog un-checked the page's selection behind it — and
 * because React's state never changed, React never re-rendered it back. The
 * component reported one thing and the DOM showed another.
 *
 * Presentational: no state, no fetching, no permission checks.
 */

export const CategorySearch = ({
  instanceId,
  value,
  onChange,
  label = "Search categories",
  className = "",
}) => (
  <div className={className}>
    <Search
      value={value}
      onChange={onChange}
      placeholder="Search..."
      ariaLabel={label}
      data-testid={`${instanceId}-search`}
    />
  </div>
);

export const CategoryGrid = ({
  instanceId,
  categoryTypeList,
  filteredList,
  selectedCategoryId,
  onSelect,
}) => (
  <div>
    {categoryTypeList?.map((categoryType) => {
      // Skip a category type with nothing under it. `null`, not `<></>` — React
      // wants no key for null, and the key-less Fragment used to warn here.
      if (
        !filteredList[categoryType.id] ||
        filteredList[categoryType.id].length <= 0
      )
        return null;

      return (
        <div key={categoryType.id} className="tw-mt-5 first:tw-mt-0">
          <Typography
            variant="sub-title-label"
            as="h3"
            className="tw-text-ink-secondary tw-mb-3"
            data-testid={`${instanceId}-cattype-${categoryType.id}`}
          >
            {categoryType.name}
          </Typography>
          <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-4 tw-gap-x-6 tw-gap-y-3">
            {filteredList[categoryType.id]?.map((item) => (
              <Radio
                key={item.id}
                /* Scoped per instance — see the docblock. */
                name={`${instanceId}-category`}
                value={item.id}
                label={item.name}
                checked={selectedCategoryId === item.id}
                onChange={() => onSelect(item)}
                data-testid={`${instanceId}-category-${item.id}`}
              />
            ))}
          </div>
        </div>
      );
    })}
  </div>
);
